import { Line } from "@react-three/drei"
import { useFrame } from "@react-three/fiber"
import { useMemo, useRef } from "react"
import { BufferGeometry, Float32BufferAttribute, Group, Vector3, type Mesh } from "three"
import type { LandingData } from "../data/types"
import { scenePresence, sectionFocus, smoothstep, type LandingMotionRef } from "../lib/landing-motion"
import { applyGroupOpacity } from "../lib/three"
import { SceneText } from "./SceneText"

export function YieldTerrain({ data, motion }: { data: LandingData; motion: LandingMotionRef }) {
  const group = useRef<Group>(null)
  const scanRef = useRef<Mesh>(null)
  const geometry = useMemo(() => terrainGeometry(data), [data])
  const inversionLines = useMemo(() => makeInversionLines(data), [data])

  useFrame(() => {
    const section = motion.current.section
    const presence = scenePresence(section, 2.25, 4.72, 0.48)
    const focus = sectionFocus(section, 3, 0.9)
    if (!group.current) return
    group.current.rotation.x = -0.05 + focus * 0.08
    group.current.rotation.y = -0.55 + smoothstep(2.35, 4.35, section) * 1.04
    group.current.position.set(1.82 - focus * 0.08, -0.2, -0.6 + presence * 0.68)
    group.current.scale.setScalar(1.46 + focus * 0.16)
    applyGroupOpacity(group.current, presence)
    if (scanRef.current) {
      scanRef.current.position.x = -2.5 + smoothstep(2.45, 3.85, section) * 5
      scanRef.current.scale.y = 0.6 + focus * 0.5
    }
  })

  return (
    <group ref={group} scale={1.58}>
      <mesh geometry={geometry}>
        <meshBasicMaterial color="#14d8b4" wireframe transparent opacity={0.5} />
      </mesh>
      {inversionLines.map((line, i) => (
        <Line key={i} points={line} color="#eab308" transparent opacity={0.62} lineWidth={1.5} />
      ))}
      <mesh ref={scanRef} position={[-2.5, 0.18, 0.2]}>
        <boxGeometry args={[0.045, 2.4, 0.06]} />
        <meshBasicMaterial color="#7af0d8" transparent opacity={0.54} />
      </mesh>
      {data.yield_curves.map((curve, i) => (
        <SceneText key={curve.country} position={[-2.65, 1.45, (i - 1.5) * 0.72]} fontSize={0.18} color="#14d8b4" fillOpacity={0.52}>
          {curve.country}
        </SceneText>
      ))}
    </group>
  )
}

function terrainGeometry(data: LandingData) {
  const curves = data.yield_curves
  const cols = 12
  const positions: number[] = []
  const indices: number[] = []
  for (let r = 0; r < curves.length; r++) {
    for (let c = 0; c < cols; c++) {
      const value = curves[r].values[c] || 0
      positions.push((c - 5.5) * 0.42, (value - 3.2) * 0.38, (r - 1.5) * 0.72)
    }
  }
  for (let r = 0; r < curves.length - 1; r++) {
    for (let c = 0; c < cols - 1; c++) {
      const a = r * cols + c
      indices.push(a, a + 1, a + cols, a + 1, a + cols + 1, a + cols)
    }
  }
  const geo = new BufferGeometry()
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3))
  geo.setIndex(indices)
  geo.computeVertexNormals()
  return geo
}

function makeInversionLines(data: LandingData) {
  const cols = 12
  return data.yield_curves.flatMap((curve, row) => {
    const z = (row - 1.5) * 0.72
    return curve.inverted_pairs.slice(0, 3).flatMap(([fromTenor, toTenor]) => {
      const from = curve.tenors.indexOf(fromTenor)
      const to = curve.tenors.indexOf(toTenor)
      if (from < 0 || to < 0 || from >= cols || to >= cols) return []
      const fromY = ((curve.values[from] || 0) - 3.2) * 0.38 + 0.045
      const toY = ((curve.values[to] || 0) - 3.2) * 0.38 + 0.045
      return [[
        new Vector3((from - 5.5) * 0.42, fromY, z),
        new Vector3((to - 5.5) * 0.42, toY, z),
      ]]
    })
  })
}
